import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tryParseRouteInfo, tryParseURL } from 'volta/utils/text-utils';

import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';

interface BvLinkSignature {
  Args: {
    href: string;
    title?: string;
    class?: string;
  };
  Blocks: {
    default: [URL | undefined];
  };
  Element: HTMLAnchorElement;
}

export default class BvLink extends Component<BvLinkSignature> {
  @service router!: RouterService;

  get url(): URL | undefined {
    return tryParseURL(this.args.href);
  }

  get isExternalLink() {
    const url = this.url;
    const routeInfo =
      url?.origin === document.location.origin ? tryParseRouteInfo(url, this.router) : undefined;
    return !routeInfo;
  }

  @action
  handleLinkClick(url: URL | undefined, event: MouseEvent): boolean | Transition {
    event.stopPropagation();
    if ((event.currentTarget as HTMLAnchorElement).target === '_blank' || this.isExternalLink) {
      return true;
    } else if (url) {
      event.preventDefault();
      return this.router.transitionTo(`${url.pathname}${url.search}${url.hash}`);
    } else {
      return false;
    }
  }

  <template>
    <a
      href={{this.url.href}}
      {{on 'click' (fn this.handleLinkClick this.url)}}
      target={{if this.isExternalLink '_blank'}}
      rel={{if this.isExternalLink 'noopener noreferrer'}}
      ...attributes
    >
      {{#if (has-block)}}
        {{yield this.url}}
      {{else}}
        {{this.url.href}}
      {{/if}}
    </a>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvLink: typeof BvLink;
    'bv-link': typeof BvLink;
  }
}
